import cx from 'classnames';
import {
    Children,
    cloneElement,
    isValidElement,
    ReactNode,
    useMemo,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CaretIcon } from 'src/components/Icons/icons';

type SortDirection = 'asc' | 'desc';

type SortState = {
    key: string;
    direction: SortDirection;
} | null;

type TableColumn<T> = {
    header: ReactNode;
    className?: string;
    sortKey?: string;
    sortValue?: (item: T) => unknown;
    sortCompare?: (a: T, b: T) => number;
};

type TableProps<T> = {
    columns: TableColumn<T>[];
    data: T[];
    renderRow: (item: T, index: number) => React.ReactElement;
    emptyMessage?: ReactNode;
    isLoading?: boolean;
    loadingRowCount?: number;
    className?: string;
    defaultSort?: SortState;
    sortQueryParamKey?: string;
    highlight?: {
        predicate: (item: T) => boolean;
        className?: string;
        columnIndex?: number;
    };
};

export const Table = <T,>({
    columns,
    data,
    renderRow,
    emptyMessage,
    isLoading = false,
    loadingRowCount = 3,
    className,
    defaultSort = null,
    sortQueryParamKey,
    highlight,
}: TableProps<T>) => {
    const location = useLocation();
    const history = useHistory();

    const sortableColumnByKey = useMemo(() => {
        const map = new Map<string, TableColumn<T>>();
        for (const column of columns) {
            if (!column.sortKey) continue;
            if (!column.sortCompare && !column.sortValue) continue;
            map.set(column.sortKey, column);
        }
        return map;
    }, [columns]);

    const parseSortFromSearch = useMemo(() => {
        return (search: string): SortState => {
            if (!sortQueryParamKey) return null;

            const params = new URLSearchParams(search);
            const raw = params.get(sortQueryParamKey);
            if (!raw) return null;

            const [key, direction] = raw.split(':');
            if (!key) return null;
            if (direction !== 'asc' && direction !== 'desc') return null;
            if (!sortableColumnByKey.has(key)) return null;

            return { key, direction };
        };
    }, [sortQueryParamKey, sortableColumnByKey]);

    const sortState = useMemo<SortState>(() => {
        const parsed = parseSortFromSearch(location.search);
        if (parsed) return parsed;
        if (!sortQueryParamKey) return null;
        if (!defaultSort) return null;
        if (!sortableColumnByKey.has(defaultSort.key)) return null;
        return defaultSort;
    }, [
        defaultSort,
        location.search,
        parseSortFromSearch,
        sortQueryParamKey,
        sortableColumnByKey,
    ]);

    const sortedData = useMemo(() => {
        if (!sortState) return data;
        const column = sortableColumnByKey.get(sortState.key);
        if (!column) return data;

        const directionFactor = sortState.direction === 'asc' ? 1 : -1;

        const decorated = data.map((item, index) => {
            const sortValue = column.sortValue
                ? column.sortValue(item)
                : undefined;
            return { item, index, sortValue };
        });

        const compareUnknown = (a: unknown, b: unknown): number => {
            const aNil = a === null || a === undefined;
            const bNil = b === null || b === undefined;
            if (aNil && bNil) return 0;
            if (aNil) return 1;
            if (bNil) return -1;

            if (typeof a === 'number' && typeof b === 'number') {
                return a - b;
            }

            return String(a).localeCompare(String(b), undefined, {
                numeric: true,
                sensitivity: 'base',
            });
        };

        decorated.sort((left, right) => {
            const primary = column.sortCompare
                ? column.sortCompare(left.item, right.item)
                : compareUnknown(left.sortValue, right.sortValue);

            if (primary !== 0) return primary * directionFactor;
            return left.index - right.index;
        });

        return decorated.map(d => d.item);
    }, [data, sortState, sortableColumnByKey]);

    const loadingRows = Array.from({ length: loadingRowCount }, (_, idx) => (
        <tr key={`loading-${idx}`}>
            <td colSpan={columns.length} className="px-4 py-3">
                <div className="h-4 w-1/3 animate-pulse rounded bg-lightPeach bg-opacity-60" />
            </td>
        </tr>
    ));

    const renderedRows = isLoading
        ? loadingRows
        : sortedData.length
        ? sortedData.map((item, index) => {
              let row = renderRow(item, index);

              if (
                  highlight &&
                  highlight.predicate(item) &&
                  isValidElement<{ children?: ReactNode; className?: string }>(
                      row,
                  )
              ) {
                  const highlightClass =
                      highlight.className ?? 'bg-lightPeach bg-opacity-40';

                  if (highlight.columnIndex === undefined) {
                      row = cloneElement(row, {
                          className: cx(row.props.className, highlightClass),
                      });
                  } else if (highlight.columnIndex >= 0) {
                      const children = Children.toArray(row.props.children);
                      const target = children[highlight.columnIndex];

                      if (isValidElement<{ className?: string }>(target)) {
                          children[highlight.columnIndex] = cloneElement(
                              target,
                              {
                                  className: cx(
                                      target.props.className,
                                      highlightClass,
                                  ),
                              },
                          );

                          row = cloneElement(row, { children });
                      }
                  }
              }

              if (isValidElement(row) && row.key == null) {
                  return cloneElement(row, { key: index });
              }

              return row;
          })
        : [
              <tr key="empty">
                  <td
                      colSpan={columns.length}
                      className="px-4 py-6 text-center text-mongooseGray"
                  >
                      {emptyMessage ?? 'No data available'}
                  </td>
              </tr>,
          ];

    const handleSortClick = (column: TableColumn<T>) => {
        if (!column.sortKey) return;
        if (!column.sortCompare && !column.sortValue) return;

        if (!sortQueryParamKey) return;

        const nextSort: SortState = (() => {
            if (!sortState || sortState.key !== column.sortKey) {
                return { key: column.sortKey, direction: 'asc' };
            }

            if (sortState.direction === 'asc') {
                return { key: sortState.key, direction: 'desc' };
            }

            return null;
        })();

        const params = new URLSearchParams(location.search);
        if (nextSort) {
            params.set(
                sortQueryParamKey,
                `${nextSort.key}:${nextSort.direction}`,
            );
        } else {
            params.delete(sortQueryParamKey);
        }

        const nextSearch = params.toString();
        const nextSearchWithPrefix = nextSearch ? `?${nextSearch}` : '';
        if (nextSearchWithPrefix === location.search) return;

        history.replace({
            pathname: location.pathname,
            search: nextSearchWithPrefix,
            hash: location.hash,
        });
    };

    return (
        <div
            className={cx(
                'overflow-x-auto rounded-xl border border-starkWhite bg-white',
                className,
            )}
        >
            <table className="w-full min-w-full table-auto text-woodyBrown">
                <thead className="text-sm">
                    <tr className="border-b border-starkWhite">
                        {columns.map((column, idx) => (
                            <th
                                key={idx}
                                className={cx(
                                    'px-4 py-3 text-left font-semibold',
                                    column.className,
                                )}
                            >
                                {column.sortKey &&
                                (column.sortCompare || column.sortValue) &&
                                Boolean(sortQueryParamKey) ? (
                                    <button
                                        type="button"
                                        className="inline-flex items-center gap-1 hover:opacity-80"
                                        onClick={() => handleSortClick(column)}
                                    >
                                        {column.header}
                                        <span
                                            className="ml-1 inline-flex flex-col"
                                            aria-hidden="true"
                                        >
                                            <span
                                                className={cx(
                                                    'relative h-2.5 w-2.5 rotate-180',
                                                    sortState?.key ===
                                                        column.sortKey &&
                                                        sortState.direction ===
                                                            'asc'
                                                        ? 'text-woodyBrown'
                                                        : 'text-mongooseGray',
                                                )}
                                            >
                                                <CaretIcon
                                                    className={
                                                        sortState?.direction ===
                                                        'asc'
                                                            ? 'stroke-[3px]'
                                                            : 'stroke-[2px]'
                                                    }
                                                />
                                            </span>
                                            <span
                                                className={cx(
                                                    'relative h-2.5 w-2.5',
                                                    sortState?.key ===
                                                        column.sortKey &&
                                                        sortState.direction ===
                                                            'desc'
                                                        ? 'text-woodyBrown'
                                                        : 'text-mongooseGray',
                                                )}
                                            >
                                                <CaretIcon
                                                    className={
                                                        sortState?.direction ===
                                                        'desc'
                                                            ? 'stroke-[3px]'
                                                            : 'stroke-[2px]'
                                                    }
                                                />
                                            </span>
                                        </span>
                                    </button>
                                ) : (
                                    column.header
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-starkWhite">
                    {renderedRows}
                </tbody>
            </table>
        </div>
    );
};
